/**
 * Debug Logger Utility
 * Provides colorful console logging that can be enabled via localStorage or cookie
 * Works in both development and production
 * 
 * Usage:
 * - Enable in browser console: localStorage.setItem('metrics_debug', 'true')
 * - Or use: window.enableMetricsDebug()
 * - Disable: localStorage.removeItem('metrics_debug') or window.disableMetricsDebug()
 * - Check status: window.isMetricsDebugEnabled()
 * 
 * In development, logging is enabled by default.
 * In production, logging is disabled unless explicitly enabled via localStorage/cookie.
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error';

interface LoggerConfig {
    prefix: string;
    color: string;
    backgroundColor?: string;
}

const LOGGER_CONFIGS: Record<string, LoggerConfig> = {
    firebase: {
        prefix: '🔥 Firebase',
        color: '#FF6B35',
        backgroundColor: '#FFF4F0',
    },
    amplitude: {
        prefix: '📊 Amplitude',
        color: '#FF6B6B',
        backgroundColor: '#FFF0F0',
    },
    facebook: {
        prefix: '📘 Facebook',
        color: '#1877F2',
        backgroundColor: '#E7F3FF',
    },
    tiktok: {
        prefix: '🎵 TikTok',
        color: '#000000',
        backgroundColor: '#FF0050',
    },
    googleAds: {
        prefix: '📈 Google Ads',
        color: '#4285F4',
        backgroundColor: '#E8F0FE',
    },
    metrics: {
        prefix: '📡 Metrics',
        color: '#9B59B6',
        backgroundColor: '#F4ECF7',
    },
    default: {
        prefix: '🔍 Debug',
        color: '#6C757D',
        backgroundColor: '#F8F9FA',
    },
};

/**
 * Check if debug logging is enabled
 * Checks localStorage first, then cookies
 */
const isDebugEnabled = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    // Check localStorage first
    const localStorageFlag = localStorage.getItem('metrics_debug');
    if (localStorageFlag === 'true' || localStorageFlag === '1') {
        return true;
    }

    // Check cookie as fallback
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'metrics_debug' && (value === 'true' || value === '1')) {
            return true;
        }
    }

    // In development, enable by default
    return process.env.NODE_ENV === 'development';
};

/**
 * Get logger config for a provider
 */
const getLoggerConfig = (provider: string): LoggerConfig => {
    return LOGGER_CONFIGS[provider.toLowerCase()] || LOGGER_CONFIGS.default;
};

/**
 * Format log message with colors
 */
const formatLog = (
    config: LoggerConfig,
    level: LogLevel,
    message: string,
    ...args: any[]
): void => {
    if (!isDebugEnabled()) {
        return;
    }

    const styles = [
        `color: ${config.color}`,
        `background-color: ${config.backgroundColor || 'transparent'}`,
        'padding: 2px 6px',
        'border-radius: 3px',
        'font-weight: bold',
    ].join(';');

    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const prefix = `%c[${timestamp}] ${config.prefix}`;

    switch (level) {
        case 'log':
            console.log(prefix, styles, message, ...args);
            break;
        case 'info':
            console.info(prefix, styles, message, ...args);
            break;
        case 'warn':
            console.warn(prefix, styles, message, ...args);
            break;
        case 'error':
            console.error(prefix, styles, message, ...args);
            break;
    }
};

/**
 * Create a logger instance for a specific provider
 */
export const createDebugLogger = (provider: string) => {
    const config = getLoggerConfig(provider);

    return {
        log: (message: string, ...args: any[]) => {
            formatLog(config, 'log', message, ...args);
        },
        info: (message: string, ...args: any[]) => {
            formatLog(config, 'info', message, ...args);
        },
        warn: (message: string, ...args: any[]) => {
            formatLog(config, 'warn', message, ...args);
        },
        error: (message: string, ...args: any[]) => {
            formatLog(config, 'error', message, ...args);
        },
        /**
         * Log event tracking with structured format
         * Uses collapsed groups by default for cleaner console output
         */
        track: (eventName: string, properties?: Record<string, any>) => {
            if (!isDebugEnabled()) {
                return;
            }

            const styles = [
                `color: ${config.color}`,
                `background-color: ${config.backgroundColor || 'transparent'}`,
                'padding: 2px 6px',
                'border-radius: 3px',
                'font-weight: bold',
            ].join(';');

            const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
            const prefix = `%c[${timestamp}] ${config.prefix}`;

            // Use groupCollapsed so logs are collapsed by default
            console.groupCollapsed(`${prefix}`, styles, `Event: ${eventName}`);
            if (properties && Object.keys(properties).length > 0) {
                console.table(properties);
            }
            console.groupEnd();
        },
    };
};

/**
 * Enable debug logging programmatically
 */
export const enableDebugLogging = (): void => {
    if (typeof window !== 'undefined') {
        localStorage.setItem('metrics_debug', 'true');
        // Also set cookie for cross-domain scenarios
        document.cookie = 'metrics_debug=true; path=/; max-age=31536000'; // 1 year
        console.log('%c✅ Metrics Debug Logging Enabled', 'color: #28a745; font-weight: bold; font-size: 14px');
    }
};

/**
 * Disable debug logging programmatically
 */
export const disableDebugLogging = (): void => {
    if (typeof window !== 'undefined') {
        localStorage.removeItem('metrics_debug');
        document.cookie = 'metrics_debug=false; path=/; max-age=0';
        console.log('%c❌ Metrics Debug Logging Disabled', 'color: #dc3545; font-weight: bold; font-size: 14px');
    }
};

/**
 * Check if debug logging is currently enabled
 */
export const isDebugLoggingEnabled = (): boolean => {
    return isDebugEnabled();
};

// Expose helper functions to window for easy browser console access
if (typeof window !== 'undefined') {
    (window as any).enableMetricsDebug = enableDebugLogging;
    (window as any).disableMetricsDebug = disableDebugLogging;
    (window as any).isMetricsDebugEnabled = isDebugLoggingEnabled;
}

